import { requestApi } from "../apiService";
import { ApiEndpoints } from "../apiEndpoints";
import { LoginFormType } from "@/schemas/auth";
import { RegisterFormTypeWithKeys } from "@/schemas/auth";
import { User } from "../types/auth";

// Pre-login: which KDF scheme this account uses (and its random salt for v3), so
// the client derives the right master key + credential before authenticating.
// `kdf_salt` is "" for v1/v2 (username-salted).
export const getKdfParams = async (
  username: string,
): Promise<{ kdf_version: number; kdf_salt: string }> => {
  const response = await requestApi({
    url: ApiEndpoints.KDF_PARAMS,
    method: "GET",
    params: { username },
  });
  return response.data;
};

// Authenticated upgrade to a newer KDF scheme: swap the credential to the new
// authSecret and store the private key re-wrapped under the new kek. For v3,
// also send the target version + random salt. Requires a Bearer token.
//
// The token is passed EXPLICITLY (same pattern as `logout`) rather than left to
// the request interceptor: this runs mid-login, before `applyAuthBundle` commits
// `username`, and the interceptor only attaches the bearer when BOTH `tk` and
// `username` are set (apiService.ts). Without the explicit header the upgrade
// request goes out tokenless → 401, silently failing every upgrade.
export const upgradeKdf = async (
  payload: {
    auth_secret: string;
    private_keys: string;
    kdf_version: number;
    kdf_salt: string;
  },
  token: string,
): Promise<{ performed: boolean; kdf_version: number; kdf_salt: string }> => {
  const response = await requestApi({
    url: ApiEndpoints.KDF_UPGRADE,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    data: payload,
  });
  // The server echoes the AUTHORITATIVE scheme the row now holds (this call may
  // have lost an upgrade race), so the caller can adopt it instead of its own.
  return {
    performed: Boolean(response.data?.performed),
    kdf_version: response.data?.kdf_version ?? payload.kdf_version,
    kdf_salt: response.data?.kdf_salt ?? payload.kdf_salt,
  };
};

export const login = async (
  payload: LoginFormType & { accepted_tc?: true; turnstileToken?: string },
): Promise<User> => {
  // If the payload is valid, make a request to the login endpoint
  const response = await requestApi({
    url: ApiEndpoints.LOGIN,
    method: "POST",
    data: payload,
  });

  //   If the request is successful, return the data
  const data: User = response.data;
  return data;
};

export const logout = async (refreshToken: string, accessToken: string) => {
  const response = await requestApi({
    url: ApiEndpoints.LOGOUT,
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: { refreshToken },
  });

  return response.data;
};

export const register = async (
  payload: RegisterFormTypeWithKeys & {
    email: string;
    accepted_tc: true;
    turnstileToken?: string;
    kdf_version?: number;
    kdf_salt?: string;
  },
): Promise<User> => {
  const response = await requestApi({
    url: ApiEndpoints.REGISTER,
    method: "POST",
    data: payload,
  });

  return response.data as User;
};

export const sendVerificationCode = async (email: string): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.SEND_VERIFICATION_CODE,
    method: "POST",
    data: { email },
  });
};

export const verifyEmail = async (
  email: string,
  code: string,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.VERIFY_EMAIL,
    method: "POST",
    data: { email, code },
  });
};

// Forgot-username recovery: the backend emails the username(s) registered to
// the address and ALWAYS responds 200 regardless of whether the email exists
// (no enumeration). The send is fire-and-forget, so a 200 means "request
// accepted", NOT "email delivered" — the caller must show a generic message.
export const forgotUsername = async (payload: {
  email: string;
  turnstileToken?: string;
}): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.FORGOT_USERNAME,
    method: "POST",
    data: payload,
  });
};

export type BatchUsersResponse = {
  usernames: string[];
  errors: string[];
};

export const registerBatch = async (
  users: RegisterFormTypeWithKeys[],
): Promise<BatchUsersResponse> => {
  const response = await requestApi({
    url: ApiEndpoints.REGISTER_BATCH,
    method: "POST",
    data: users,
  });
  return response.data;
};
